/**
 * Funções de formatação reutilizáveis
 */

/**
 * Formata valor como moeda brasileira
 * @param {number} value - Valor numérico
 * @returns {string} - Valor formatado (ex: R$ 1.234,56)
 */
export const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);
};

/**
 * Formata quilometragem
 * @param {number} value - Valor em KM
 * @returns {string} - KM formatado (ex: 1.234,5)
 */
export const formatKm = (value) => {
    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
    }).format(value);
};

/**
 * Formata valor brasileiro (vírgula como decimal)
 * @param {string} value - String com valor
 * @returns {string} - Valor formatado
 */
export const formatValueBRL = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const strValue = String(value).replace('.', ',');
    const commaIndex = strValue.indexOf(',');

    if (commaIndex === -1) {
        return strValue;
    }

    const integerPart = strValue.substring(0, commaIndex);
    let decimalPart = strValue.substring(commaIndex + 1);

    // Limita a 2 casas decimais
    if (decimalPart.length > 2) {
        decimalPart = decimalPart.substring(0, 2);
    }

    return `${integerPart},${decimalPart}`;
};

/**
 * Converte string formatada BR para número
 * @param {string} value - String com vírgula como decimal
 * @returns {number|null} - Número parseado ou null
 */
export const parseValueBRL = (value) => {
    if (value === null || value === undefined || value === '') return null;

    // Se já for número, retorna ele mesmo
    if (typeof value === 'number') return value;

    const strValue = String(value);

    // Se contém vírgula, tratamos como formato brasileiro (milhar com ponto, decimal com vírgula)
    // Ex: "1.000,50" -> "1000.50"
    if (strValue.includes(',')) {
        const cleanedValue = strValue
            .replace(/\./g, '')
            .replace(',', '.');
        const parsed = parseFloat(cleanedValue);
        return isNaN(parsed) ? null : parsed;
    }

    // Se não contém vírgula, assumimos que já é um formato numérico válido (ponto-decimal ou inteiro)
    // Isso evita que "10.5" seja transformado em "105" por remover pontos de milhar erroneamente
    const parsed = parseFloat(strValue);
    return isNaN(parsed) ? null : parsed;
};

/**
 * Formata número com casas decimais específicas
 * @param {number} value - Valor numérico
 * @param {number} decimals - Quantidade de casas decimais
 * @returns {string} - Valor formatado
 */
export const formatNumber = (value, decimals = 2) => {
    // Se o valor for inteiro, não mostra casas decimais (min=0)
    // Se tiver decimal, mostra até a quantidade especificada
    const hasDecimals = Number(value) % 1 !== 0;

    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: hasDecimals ? decimals : 0,
        maximumFractionDigits: decimals
    }).format(value);
};

/**
 * Formata data para padrão brasileiro
 * @param {string|Date} date - Data em ISO ou objeto Date
 * @returns {string} - Data formatada (dd/mm/aaaa)
 */
export const formatDate = (date) => {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleDateString('pt-BR');
};

/**
 * Calcula o tempo decorrido entre uma data e hoje (humanizado)
 * @param {string|Date} startDate - Data de início
 * @returns {string} - Tempo decorrido (ex: "2 meses e 1 dia")
 */
export const formatTimeElapsed = (startDate, short = false) => {
    if (!startDate) return '-';
    
    // Converte string YYYY-MM-DD para Date tratanto hifen/barra
    const start = typeof startDate === 'string' ? new Date(startDate.replace(/-/g, '/')) : startDate;
    if (!(start instanceof Date) || isNaN(start.getTime())) return '-';
    
    const now = new Date();
    // Zera horas para comparação puramente de datas
    start.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);

    let years = now.getFullYear() - start.getFullYear();
    let months = now.getMonth() - start.getMonth();
    let days = now.getDate() - start.getDate();

    if (days < 0) {
        months -= 1;
        // Pega último dia do mês anterior ao atual
        const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        days += prevMonth;
    }
    if (months < 0) {
        years -= 1;
        months += 12;
    }

    const parts = [];
    if (short) {
        if (years > 0) parts.push(`${years}a`);
        if (months > 0) parts.push(`${months}m`);
        if (days > 0 || (years === 0 && months === 0)) parts.push(`${days}d`);
        return parts.join(' ');
    }

    if (years > 0) parts.push(`${years} ${years === 1 ? 'ano' : 'anos'}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? 'mês' : 'meses'}`);
    if (days > 0 || (years === 0 && months === 0)) parts.push(`${days} ${days === 1 ? 'dia' : 'dias'}`);

    if (parts.length === 0) return 'hoje';
    if (parts.length === 1) return parts[0];
    
    const lastPart = parts.pop();
    return `${parts.join(', ')} e ${lastPart}`;
};

/**
 * Formata data e hora para padrão brasileiro
 * @param {string|Date} date - Data em ISO ou objeto Date
 * @returns {string} - Data e hora formatadas
 */
export const formatDateTime = (date) => {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleString('pt-BR');
};

/**
 * Formata apenas a hora de uma data (HH:mm)
 * @param {string|Date} date - Data em ISO ou objeto Date
 * @returns {string} - Hora formatada ou '00:00' se inválido
 */
export const formatTimeOnly = (date) => {
    if (!date) return '00:00';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return '00:00';
    return dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

/**
 * Formata o mês por extenso de uma data (ex: Janeiro)
 * @param {string|Date} date - Data em ISO ou objeto Date
 * @returns {string} - Mês por extenso (capitalizado)
 */
export const formatMonthLong = (date) => {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return '';
    const month = dateObj.toLocaleDateString('pt-BR', { month: 'long' });
    return month.charAt(0).toUpperCase() + month.slice(1);
};

/**
 * Formata CPF (XXX.XXX.XXX-XX)
 * @param {string} v - CPF bruto
 * @returns {string} - CPF formatado
 */
export const formatCPF = (v) => {
    if (!v) return '';
    const d = v.replace(/\D/g, '').slice(0, 11);
    let r = d;
    if (d.length > 9) r = d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    else if (d.length > 6) r = d.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
    else if (d.length > 3) r = d.replace(/(\d{3})(\d{1,3})/, '$1.$2');
    return r;
};

/**
 * Formata telefone: (DD) 9XXXX-XXXX ou (DD) XXXX-XXXX
 * @param {string} raw - Telefone bruto
 * @returns {string} - Telefone formatado
 */
export const formatPhone = (raw) => {
    if (!raw) return '';
    let v = raw.replace(/\D/g, '').slice(0, 11);
    if (v.length <= 2) return v;
    const ddd = v.slice(0, 2);
    const rest = v.slice(2);
    if (rest.length <= 4) return `(${ddd}) ${rest}`;
    if (rest.length <= 8) return `(${ddd}) ${rest.slice(0, rest.length - 4)}-${rest.slice(-4)}`;
    return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(-4)}`;
};

/**
 * Capitaliza as primeiras letras de cada palavra (Nome Próprio)
 * @param {string} s - String original
 * @returns {string} - String capitalizada
 */
export const capitalizeName = (s) => {
    if (!s) return '';
    return s.split(' ').map(p => p ? (p.charAt(0).toUpperCase() + p.slice(1)) : '').join(' ');
};

/**
 * Formata horas decimais no padrão "Xh Ymin"
 * @param {number} decimalHours - Horas em formato decimal (ex: 2.5)
 * @returns {string} - Horas formatadas (ex: 2h 30min)
 */
export const formatWorkHours = (decimalHours) => {
    if (!decimalHours || isNaN(decimalHours) || decimalHours <= 0) return '0h';

    const h = Math.floor(decimalHours);
    const m = Math.floor((decimalHours - h) * 60);

    if (h === 0 && m > 0) return `${m}min`;
    if (m === 0) return `${h}h`;

    return `${h}h ${m}min`;
};

/**
 * Formata segundos no padrão "HH:MM:SS"
 * @param {number} totalSeconds - Total de segundos
 * @returns {string} - Tempo formatado (ex: 02:30:05)
 */
export const formatTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

/**
 * Formata um número para ser exibido em um input de moeda (reais)
 * Garante que sempre tenha duas casas decimais e vírgula.
 * @param {number|string} value - Valor a ser formatado
 * @returns {string} - Valor formatado (ex: 300 -> "300,00")
 */
export const formatCurrencyInput = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = parseValueBRL(value) || 0;
    return num.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};
